/* TreatmentLockedCard — iter98.4
 *
 * Friendly placeholder shown on the patient portal's "My Aligner Case"
 * page (Treatment tab + Dashboard) while their doctor hasn't yet
 * flipped the activation switch. Keeps internal clinic operations
 * (planning / production / shipping) out of the patient's view and
 * ties the moment of value to the actual aligner fitting.
 *
 * Self-hides when the patient is either:
 *   • Already activated (full schedule + tracker show instead)
 *   • Has no case at all (Dashboard's standard empty state handles it)
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkle, Lock, ChatCircleDots } from "@phosphor-icons/react";

export default function TreatmentLockedCard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/patient/me/aligner-progress")
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setData(null); });
        return () => { cancelled = true; };
    }, []);

    // Render NOTHING while loading or when the gate doesn't apply. The
    // sibling MyAlignerProgress / MyWearSchedule cards self-render the
    // appropriate state instead.
    if (!data) return null;
    if (!data.gated) return null;

    const doctorName = data.case?.doctor_name || "your doctor";
    const caseCode = data.case?.code;

    return (
        <div
            className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/[0.04] via-card to-card"
            data-testid="treatment-locked-card"
        >
            {/* Soft sparkle pattern in the background — keeps the card feeling
                anticipatory rather than "wait" / "loading". */}
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-40 h-40 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

            <div className="relative px-6 py-8 sm:py-10 text-center max-w-md mx-auto">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-primary text-white grid place-items-center mb-4">
                    <Lock size={22} weight="bold" />
                </div>

                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight leading-tight">
                    Your treatment dashboard is on its way
                </h2>

                <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed">
                    We&apos;ll unlock this page the moment <span className="font-semibold text-foreground">{doctorName}</span> fits
                    your aligners at the clinic. Until then, your case is in safe hands with our planning team.
                </p>

                {caseCode && (
                    <div className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-full bg-secondary/60 border border-border">
                        <Sparkle size={11} weight="fill" className="text-primary" />
                        <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-muted-foreground">
                            Case
                        </span>
                        <span className="text-xs font-semibold tabular-nums">{caseCode}</span>
                    </div>
                )}

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-left">
                    <PreviewTile
                        icon={Sparkle}
                        title="Wear schedule"
                        body="Day-by-day plan you can follow"
                    />
                    <PreviewTile
                        icon={ChatCircleDots}
                        title="Switch tracking"
                        body="One-tap log when you move aligners"
                    />
                </div>

                <p className="text-[11px] text-muted-foreground mt-5 leading-snug">
                    In the meantime, you can still book appointments and chat with your clinic from the menu above.
                </p>
            </div>
        </div>
    );
}

function PreviewTile({ icon: Icon, title, body }) {
    return (
        <div className="rounded-xl border border-border bg-card p-3 flex items-start gap-2.5">
            <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0 mt-0.5">
                <Icon size={13} weight="duotone" />
            </span>
            <div className="min-w-0">
                <div className="text-xs font-bold leading-tight">{title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{body}</div>
            </div>
        </div>
    );
}
